import { Box, HStack } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { Radio } from "lucide-react";
import type { BotRecord, RuntimeActivity } from "../types/domain";
import { StatusPill } from "./ui";

const labels: Record<RuntimeActivity, string> = {
  TRACKING: "TRACKING NOW",
  WAITING_FOR_MARKET: "TRACKING · MARKET CLOSED",
  STARTING: "STARTING RUNTIME",
  RECOVERING: "RECOVERING",
  PAUSED: "NOT TRACKING · PAUSED",
  STOPPED: "NOT TRACKING · STOPPED",
  FAULTED: "RUNTIME FAULTED",
  STALE: "RUNTIME STALE",
};

export function runtimeActivityTone(activity?: RuntimeActivity) {
  if (activity === "TRACKING") return "positive" as const;
  if (activity === "WAITING_FOR_MARKET" || activity === "STARTING" || activity === "RECOVERING") {
    return "warning" as const;
  }
  if (activity === "FAULTED" || activity === "STALE") return "negative" as const;
  return "neutral" as const;
}

export function runtimeActivityLabel(bot?: BotRecord, compact = false): string {
  const activity = bot?.runtimeActivity;
  if (!activity) return bot?.status === "RUNNING" ? "CHECKING RUNTIME" : "RUNTIME INACTIVE";
  if (!compact) return labels[activity];
  if (activity === "WAITING_FOR_MARKET") return "TRACKING · CLOSED";
  if (activity === "TRACKING") return "TRACKING";
  return labels[activity];
}

export function runtimeHeartbeatAge(value?: string): string {
  if (!value) return "No current runtime heartbeat";
  const ageSeconds = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1_000));
  if (!Number.isFinite(ageSeconds)) return "Invalid heartbeat";
  if (ageSeconds < 2) return "Heartbeat now";
  if (ageSeconds < 60) return `Heartbeat ${ageSeconds}s ago`;
  return `Heartbeat ${Math.floor(ageSeconds / 60)}m ago`;
}

export function RuntimeActivityPill({ bot, compact = false }: { bot?: BotRecord; compact?: boolean }) {
  return (
    <StatusPill tone={runtimeActivityTone(bot?.runtimeActivity)}>
      <HStack gap="1.5">
        <Box as={Radio} boxSize="3" />
        {runtimeActivityLabel(bot, compact)}
      </HStack>
    </StatusPill>
  );
}

export function ExecutionModePills({ bot }: { bot?: BotRecord }) {
  if (!bot) return <StatusPill>IDLE</StatusPill>;
  const modeTone = bot.mode === "LIVE" ? "negative" : bot.mode === "PAPER" ? "warning" : "neutral";
  return (
    <HStack gap="1.5" title={bot.mode === "PAPER" ? "PAPER: simulated orders using broker market data" : undefined}>
      <StatusPill tone={modeTone}>{bot.mode}</StatusPill>
      {bot.mode === "LIVE" && bot.brokerEnvironment && (
        <StatusPill tone={bot.brokerEnvironment === "DEMO" ? "info" : "negative"}>
          {bot.brokerEnvironment} ACCOUNT
        </StatusPill>
      )}
    </HStack>
  );
}

const runtimePulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: .3; transform: scale(.72); }
`;

function runtimePulseState(bot?: BotRecord) {
  // Heartbeat health belongs to the runtime process, not to market-session
  // availability. A healthy runtime keeps pulsing while the market is closed.
  const active = Boolean(bot?.status === "RUNNING" && bot.runtimeAlive);
  const transitioning = bot?.runtimeActivity === "STARTING" || bot?.runtimeActivity === "RECOVERING";
  return {
    active,
    transitioning,
    color: active ? "status.positive" : transitioning ? "status.warning" : "status.negative",
    state: active ? "active" : transitioning ? "recovering" : "inactive",
    label: active ? "LIVE" : transitioning ? "SYNCING" : "OFFLINE",
  } as const;
}

export function RuntimePulseDot({ bot, size = "9px" }: { bot?: BotRecord; size?: string }) {
  const { active, transitioning, color, state } = runtimePulseState(bot);
  const mode = bot?.mode ?? "UNKNOWN MODE";
  return (
    <Box
      as="span"
      role="status"
      aria-label={`${mode} runtime ${state}`}
      title={`${mode} runtime ${state}`}
      display="inline-block"
      flexShrink="0"
      boxSize={size}
      borderRadius="999px"
      bg={color}
      boxShadow={active ? "0 0 0 3px rgba(16,185,129,.14), 0 0 9px rgba(16,185,129,.7)" : "0 0 0 3px rgba(244,63,94,.12)"}
      animation={active || transitioning ? `${runtimePulse} 1.25s ease-in-out infinite` : undefined}
    />
  );
}

export function RuntimeHeartbeatStatus({ bot, size = "10px" }: { bot?: BotRecord; size?: string }) {
  const { color, label } = runtimePulseState(bot);
  const age = runtimeHeartbeatAge(bot?.runtimeHeartbeat);
  return (
    <HStack gap="2" align="center" aria-label={`${label}. ${age}`} title={age}>
      <RuntimePulseDot bot={bot} size={size} />
      <Box as="span" color={color} fontSize="10px" fontFamily="mono" fontWeight="800" lineHeight="1.35">
        {label}
      </Box>
    </HStack>
  );
}
